import * as React from 'react';
import { Link } from '@tanstack/react-router';
import { t } from '@reos/i18n';
import { PageHead } from '../components/page-head.js';
import { guideStepTarget, type GuideStepId } from '../lib/guide-api.js';

const cs = { locale: 'cs' } as const;

/**
 * `/napoveda` (krok APP2 bod 3, bod 21 zadani) — NAPOVEDA V APLIKACI.
 *
 * Do APP2 cesta `/napoveda` neexistovala: makler, ktery ji napsal (nebo na ni
 * sel z menu jine aplikace), skoncil na 404. Napoveda je ZAMERNE JEDNA
 * STRANKA — ne vetveny system clanku: pilot ma sedm uloh a ke kazde patri
 * jedna veta a jeden odkaz, kde se to dela.
 *
 * TEXTY NEJSOU DRUHA KOPIE. Temata jsou KROKY PRUVODCE „Co si vyzkoušet"
 * (`apps/api/src/guide/guide.ts`, karta na Dnesku): nadpis i veta „proc" se
 * ctou z TEHOZ katalogu (`webAdmin.guide.steps.*`) a odkaz sklada TATAZ
 * funkce jako „Ukaž mi" na karte (`guideStepTarget`). Kdyz se zmeni pruvodce,
 * zmeni se napoveda s nim; dve kopie by se rozesly.
 */

/**
 * Poradi temat. `Record` je ZAMERNY: novy krok pruvodce, ktery by tady
 * nedostal radek, neprojde typovou kontrolou (`GuideStepId` je z API).
 */
const TOPIC_ORDER: Readonly<Record<GuideStepId, number>> = {
  case: 1,
  report: 2,
  intake: 3,
  listing: 4,
  acquisition: 5,
  contract: 6,
  mailbox: 7,
};

const TOPICS: readonly GuideStepId[] = (Object.keys(TOPIC_ORDER) as GuideStepId[]).sort(
  (a, b) => TOPIC_ORDER[a] - TOPIC_ORDER[b],
);

export function NapovedaPage(): React.JSX.Element {
  return (
    <div data-testid="napoveda-page">
      <PageHead title={t('webAdmin.help.title', cs)} subtitle={t('webAdmin.help.perex', cs)} />

      <div className="rh-a2-page">
        <section className="rh-a2-card" data-testid="napoveda-steps">
          <div className="rh-a2-card__head">
            <h2>{t('webAdmin.help.stepsHeading', cs)}</h2>
          </div>
          <ul className="rh-a2-rows">
            {TOPICS.map((step) => (
              <li key={step}>
                <span className="rh-a2-grow">
                  <span className="rh-a2-title">{t(`webAdmin.guide.steps.${step}.title`, cs)}</span>
                  <span className="rh-a2-meta">{t(`webAdmin.guide.steps.${step}.why`, cs)}</span>
                </span>
                {/*
                  Odkaz je `<a>`, ne `Link`: cile pruvodce nesou hash a query
                  (`guideStepTarget`), takze je sklada jedna funkce jako text
                  cesty — typovany `Link` by potreboval vlastni rozklad, tedy
                  druhou kopii teze pravdy.
                */}
                <a className="rh-a2-link" href={guideStepTarget(step, { caseId: null })} data-testid={`napoveda-step-${step}`}>
                  {t('webAdmin.help.openStep', cs)}
                </a>
              </li>
            ))}
          </ul>
        </section>

        <section className="rh-a2-card" data-testid="napoveda-search">
          <div className="rh-a2-card__head">
            <h2>{t('webAdmin.help.searchHeading', cs)}</h2>
          </div>
          <p style={{ margin: 0 }}>{t('webAdmin.help.searchBody', cs)}</p>
        </section>

        <section className="rh-a2-card" data-testid="napoveda-team">
          <div className="rh-a2-card__head">
            <h2>{t('webAdmin.help.teamHeading', cs)}</h2>
          </div>
          <p style={{ marginTop: 0 }}>{t('webAdmin.help.teamBody', cs)}</p>
          <Link
            to="/nastaveni"
            search={{ tab: 'kancelar' }}
            className="rh-a2-link"
            data-testid="napoveda-team-link"
          >
            {t('webAdmin.help.teamLink', cs)}
          </Link>
        </section>

        <section className="rh-a2-card" data-testid="napoveda-coming-soon">
          <div className="rh-a2-card__head">
            <h2>{t('webAdmin.help.comingSoonHeading', cs)}</h2>
          </div>
          <p style={{ marginTop: 0 }}>{t('webAdmin.help.comingSoonBody', cs)}</p>
          <Link to="/co-chystame" className="rh-a2-link" data-testid="napoveda-coming-soon-link">
            {t('webAdmin.help.comingSoonLink', cs)}
          </Link>
        </section>
      </div>
    </div>
  );
}
